import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Copy, KeyRound, Users, CheckCircle2, Clock } from "lucide-react";

const REASONS = [
  ["forgot", "Owner forgot PIN"], ["lost_device", "Lost device / access"], ["unauthorized", "Suspected unauthorized access"],
  ["client_request", "Client requested emergency recovery"], ["other", "Other"],
];
const FORCE = ["lost_device", "unauthorized"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

function RecoveryStatus({ u }) {
  const r = u.pin_recovery || {};
  if (r.status === "pending") return (
    <div className="text-[11px] rounded-md border border-amber-500/40 bg-amber-500/10 p-2 space-y-0.5" data-testid={`pin-status-${u.id}`}>
      <p className="font-semibold text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Temporary PIN issued</p>
      <p>Expires: <b>{fmt(r.expires_at)}</b></p><p>Permanent PIN change: <b>Pending</b></p>
      <p className="text-muted-foreground">Issued by {r.issued_by} · {r.reason}{r.sign_out_all ? " · all sessions terminated" : ""}</p>
    </div>);
  if (r.status === "completed") return (
    <div className="text-[11px] rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2" data-testid={`pin-status-${u.id}`}>
      <p className="font-semibold text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Recovery completed</p>
      <p>New permanent PIN established: <b>{fmt(r.completed_at)}</b></p>
    </div>);
  if (r.status === "expired") return <p className="text-[11px] text-rose-500" data-testid={`pin-status-${u.id}`}>Temporary PIN expired {fmt(r.expires_at)}. Recovery not completed.</p>;
  return null;
}

// Super Admin → Tenant → Users: list + Emergency Owner PIN Reset.
export default function TenantUsersModal({ client, onClose }) {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [f, setF] = useState({ reason: "forgot", reason_note: "", expires_hours: 2, sign_out_all: false, identity_verified: false, password: "", code: "" });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => client && api.get(`/superadmin/clients/${client.id}/users`).then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { setTarget(null); setIssued(null); load(); }, [client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const forced = FORCE.includes(f.reason);
  const issue = async () => {
    setErr("");
    if (!f.identity_verified) { setErr("Confirm the client's identity/request was verified."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/clients/${client.id}/users/${target.id}/emergency-pin-reset`,
        { ...f, sign_out_all: forced || f.sign_out_all, code: f.code || null });
      setIssued(data); setF({ ...f, password: "", code: "" }); load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(issued.temporary_pin); toast.success("Temporary PIN copied"); };

  return (
    <Dialog open={!!client} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sa-users-modal">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Users className="w-5 h-5" /> Users · {client?.business_name}</DialogTitle>
          <DialogDescription>Tenant staff accounts. NEEC can never view any user's password or permanent PIN.</DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-3" data-testid="sa-pin-issued">
            <div className="rounded-xl border-2 border-amber-500 bg-amber-500/10 p-4 text-center">
              <p className="text-xs font-bold tracking-widest text-amber-600">TEMPORARY RECOVERY PIN</p>
              <p className="mono text-4xl font-extrabold tracking-[0.35em] my-2" data-testid="sa-temp-pin">{issued.temporary_pin}</p>
              <Button variant="outline" size="sm" onClick={copy} data-testid="sa-copy-temp-pin"><Copy className="w-4 h-4 mr-1.5" /> Copy temporary PIN</Button>
            </div>
            <div className="text-sm space-y-1">
              <p>Owner: <b>{issued.owner?.name}</b> ({issued.owner?.email})</p>
              <p>Expires: <b>{fmt(issued.expires_at)}</b></p>
              <p>Permanent PIN change: <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 inline-flex">Pending</Badge></p>
              <p>All sessions terminated: <b>{issued.sign_out_all ? "Yes" : "No"}</b></p>
            </div>
            <p className="text-xs text-rose-500 font-medium" data-testid="sa-temp-pin-warning">This PIN cannot be viewed again after you leave this screen. Generate a new recovery PIN if it is lost.</p>
            <p className="text-xs text-muted-foreground">Give it to the Owner through a verified channel. They sign in, enter it under Security → Owner PIN, and must create a new permanent PIN before it can be used for approvals.</p>
            <Button className="w-full" variant="outline" onClick={() => { setIssued(null); setTarget(null); }} data-testid="sa-pin-done">Done</Button>
          </div>
        ) : target ? (
          <div className="space-y-3" data-testid="sa-emergency-reset-form">
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-sm flex gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-500 shrink-0" />
              <p>Emergency reset for <b>{target.name}</b> ({target.email}). The Owner's current PIN is invalidated immediately and a random single-use temporary PIN is issued.</p>
            </div>
            <div><Label className="text-xs">Reason</Label>
              <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="sa-pin-reason">
                {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            {f.reason === "other" && <div><Label className="text-xs">Describe the reason</Label><Input value={f.reason_note} onChange={(e) => setF({ ...f, reason_note: e.target.value })} data-testid="sa-pin-reason-note" /></div>}
            <div><Label className="text-xs">Temporary PIN expires in</Label>
              <div className="flex gap-1.5 mt-1">{[1, 2, 4, 8, 24].map((h) => (
                <Button key={h} type="button" size="sm" variant={f.expires_hours === h ? "default" : "outline"} className={f.expires_hours === h ? "bg-violet-600 hover:bg-violet-700 text-white" : ""} onClick={() => setF({ ...f, expires_hours: h })} data-testid={`sa-pin-exp-${h}`}>{h}h</Button>
              ))}</div></div>
            <label className={`flex items-start gap-2 text-sm rounded-lg border p-3 ${forced ? "border-rose-500/40 bg-rose-500/5" : "border-border"}`}>
              <input type="checkbox" className="mt-0.5" checked={forced || f.sign_out_all} disabled={forced} onChange={(e) => setF({ ...f, sign_out_all: e.target.checked })} data-testid="sa-pin-signout" />
              <span>Sign Owner out of all devices/sessions{forced && <span className="block text-[11px] text-rose-500">Required for this reason. Cannot be disabled.</span>}</span>
            </label>
            <label className="flex items-start gap-2 text-sm rounded-lg border border-border p-3">
              <input type="checkbox" className="mt-0.5" checked={f.identity_verified} onChange={(e) => setF({ ...f, identity_verified: e.target.checked })} data-testid="sa-pin-identity" />
              <span>Client identity/request verified <span className="block text-[11px] text-muted-foreground">Recorded in the audit log. No identity documents are stored.</span></span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Your Super Admin password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="sa-pin-password" /></div>
              <div><Label className="text-xs">Authenticator code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} className="mono" data-testid="sa-pin-code" /></div>
            </div>
            {err && <p className="text-sm text-rose-500" data-testid="sa-pin-error">{err}</p>}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setTarget(null)} data-testid="sa-pin-cancel">Cancel</Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy} onClick={issue} data-testid="sa-pin-issue">{busy ? "Issuing…" : "Issue Temporary PIN"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`sa-user-${u.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{u.name} <Badge variant="outline" className="ml-1 capitalize text-[10px]">{u.role}</Badge>{!u.active && <Badge className="ml-1 bg-rose-500/15 text-rose-500 text-[10px]">Inactive</Badge>}</div>
                    <p className="text-xs text-muted-foreground mono truncate">{u.email}</p>
                    <p className="text-[11px] text-muted-foreground">PIN: {u.has_pin ? `set${u.pin_changed_at ? ` · changed ${fmt(u.pin_changed_at)}` : ""}` : "not set"}</p>
                  </div>
                  {u.role === "owner" && (
                    <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-600 shrink-0" onClick={() => { setErr(""); setTarget(u); }} data-testid={`sa-emergency-pin-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Emergency PIN Reset
                    </Button>
                  )}
                </div>
                <RecoveryStatus u={u} />
              </div>
            ))}
            {users.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No users found.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
